import { Box, Grid, GridItem, Heading, Text, useToast } from "@chakra-ui/react";
import LegendlessDonutChart from "../../../../../../../../components/LegendlessDonutChart/LegendlessDonutChart";
import { useGetNumberOfSupplierQuestionnairesByStatus } from "../../../../../../../../hooks/query/reports";
import { QUESTIONNAIRE_STATUSES, showErrorToast } from "../../../../../../../../utils";
import { roundByLargestRemainderMethod } from "../../../../../../../Dashboard/helpers";
import { Card } from "../../../../../../../../components/Card";
import { useTranslation } from "react-i18next";
import * as React from "react";
import { CenterLabelLayer } from "./CenterLabelLayer";

function QuestionnairesProgressByYear({ categoryId }) {
  const { t } = useTranslation("translation");
  const toast = useToast();

  const {
    data: questionnairesProgressData
  } = useGetNumberOfSupplierQuestionnairesByStatus(categoryId, {
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  const mapData = (yearData, year) => {
    const mappedData = [];

    const supplierQuestionnairesStatuses = [
      {
        id: 1,
        title: t("dashboard.suppliersEngagementChart.types.new"),
        key: QUESTIONNAIRE_STATUSES.NEW,
        color: "hsl(209, 62%, 50%)"
      },
      {
        id: 2,
        title: t("dashboard.suppliersEngagementChart.types.inProgress"),
        key: QUESTIONNAIRE_STATUSES.IN_PROGRESS,
        color: "hsl(24, 75%, 50%)"
      },
      {
        id: 3,
        title: t("dashboard.suppliersEngagementChart.types.finalised"),
        key: QUESTIONNAIRE_STATUSES.FINALIZED,
        color: "hsl(148, 48%, 43%)"
      }
    ];

    const totalValueForYear = yearData.reduce((n, { count }) => n + count, 0);

    yearData
      .filter(el => el.count > 0)
      .forEach(el => {
        const foundStatus = supplierQuestionnairesStatuses.find(statusObj => statusObj.key === el.status) ?? {};
        mappedData.push({
          ...foundStatus,
          value: el.count,
          percentageValue: (el.count / totalValueForYear) * 100,
          year: year
        });
      });

    const roundedValues = roundByLargestRemainderMethod(mappedData.map(e => e.percentageValue));
    mappedData.forEach((el, index) => {
      el.percentageValue = roundedValues[index];
    });

    return mappedData;
  };

  const getTooltip = (tooltipObj) => {
    const { datum: { data } } = tooltipObj;
    const formattedPercentageValue = data.percentageValue && data.percentageValue > 0 ?
      data.percentageValue.toLocaleString("en-GB", { maximumFractionDigits: 1 }) : "";

    const elIdx = Object.keys(questionnairesProgressData).findIndex(item => item.toString() === data.year);
    // Since we display donut charts in 4 columns per row, this is how we evaluate if the current chart
    // will be displayed in the first column
    const isInFirstColumn = elIdx !== -1 && ((elIdx + 1) % 4 === 1);

    return (
      <Box position="relative">
        <Card
          position="absolute"
          minWidth={"350px"}
          padding={3}
          left={isInFirstColumn ? "100%" : null}
          right={!isInFirstColumn ? "100%" : null}
        >
          <Text fontSize="sm" textAlign="start">
            <b>{data.title}</b> for {data.year} - <b>{data.value} Supplier{data.value > 1 ? "s" : ""} ({formattedPercentageValue}%)</b>
          </Text>
        </Card>
      </Box>);
  };

  const getLabel = (el) => {
    return <>
      <tspan x="0" dy="-0.6em">{el.data?.title ?? ""}</tspan>
      <tspan x="0" dy="1.2em">{el.data.value && el.data.value > 0 ? el.data.value : ""}</tspan>
    </>;
  };

  return (
    <Box>
      <Heading size="md" marginBottom={4}>
        {t("reportsPage.suppliersData.supplierEngagement.progressByYear")}
      </Heading>
      {questionnairesProgressData && typeof questionnairesProgressData !== "undefined" &&
      Object.keys(questionnairesProgressData).length > 0 ?
        <Grid templateColumns="repeat(4, 1fr)" gap={2}>
          {
            Object.keys(questionnairesProgressData).map(yearKey => {
              return <GridItem
                key={`progress-chart-container-${yearKey}`}
                height={"250px"}
                textAlign="center"
              >
                <LegendlessDonutChart
                  data={mapData(questionnairesProgressData[yearKey], yearKey)}
                  getTooltip={getTooltip}
                  getLabel={getLabel}
                  getLabelsTextColor={() => "white"}
                  CustomLayerComponent={CenterLabelLayer(yearKey)}
                />
              </GridItem>;
            })
          }
        </Grid>
        : <Text fontSize="lg" fontWeight="bold">
          {t("noDataAvailable")}
        </Text>
      }
    </Box>
  );
}

export default QuestionnairesProgressByYear;